import React, { useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import axios from 'axios';
import api from '../../services/api';

const LeadershipPage = () => {
  const [activeTab, setActiveTab] = useState("team"); // team | directors
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [banner, setBanner] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [directors, setDirectors] = useState([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const baseUrl = backendUrl.endsWith('/api') ? backendUrl.replace('/api', '') : backendUrl;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Banner
        const bannerResponse = await api.get("/admin/banners/active?page_type=partnership");
        if (bannerResponse.data.success && bannerResponse.data.data) {
          const partnershipBanner = Array.isArray(bannerResponse.data.data)
            ? bannerResponse.data.data[0]
            : bannerResponse.data.data;
          setBanner(partnershipBanner);
        }

        // Fetch Leadership Team
        const leadershipResponse = await api.get("/leadership/active");
        const allMembers = leadershipResponse.data.data || [];

        const processMember = (m) => {
          let bioArray = [];
          try {
            if (typeof m.bio === 'string') {
              bioArray = JSON.parse(m.bio);
            } else if (Array.isArray(m.bio)) {
              bioArray = m.bio;
            }
          } catch (e) {
            console.error("Error parsing bio", e);
            bioArray = [];
          }

          return {
            ...m,
            image: m.image_url ? (m.image_url.startsWith('http') || m.image_url.startsWith('/img') ? m.image_url : `${baseUrl}${m.image_url.startsWith('/') ? '' : '/'}${m.image_url}`) : null,
            bio: bioArray
          };
        };

        const team = allMembers.filter(m => m.type === 'management_team').sort((a, b) => a.order_index - b.order_index).map(processMember);
        const board = allMembers.filter(m => m.type === 'board_of_directors').sort((a, b) => a.order_index - b.order_index).map(processMember);

        setTeamMembers(team);
        setDirectors(board);

      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Handle Escape key press to close popups
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (selectedMember) {
          setSelectedMember(null);
        }
        if (selectedDirector) {
          setSelectedDirector(null);
        }
      }
    };

    // Add event listener when a popup is open
    if (selectedMember || selectedDirector) {
      document.addEventListener("keydown", handleKeyDown);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedMember, selectedDirector]);

  const bannerImage = banner
    ? (banner.image_url.startsWith('http') || banner.image_url.startsWith('/img/')
      ? banner.image_url
      : `${baseUrl}${banner.image_url.startsWith('/') ? '' : '/'}${banner.image_url}`)
    : "/img/Banners/Leadership-Banner.webp"; // Default fallback

  return (
    <>
      <SEO
        title="Aham Housing Finance Partnership | Home Loan Channel Partner"
        description="Become an Aham Housing Finance channel partner. Earn attractive payouts on home loan leads with simple onboarding, fast approvals and full sales support."
        keywords="Aham Housing Finance partnership, Aham partnership, partner with Aham Housing Finance, housing finance partnership, home loan partner program, Aham business partnership, loan sourcing partner, housing finance channel partner, DSA partnership, connector partnership, referral partner home loans, co-lending partnership, affordable housing finance partner, strategic partnership Aham, financial services partnership, mortgage partner program, home loan distribution partner, partner network Aham Housing Finance, lending partnership India, Aham partner opportunities"
        canonical="https://ahamhfc.com/aham-housing-finance-partnership-channel-partner"
        ogTitle="Aham Housing Finance Partnership | Home Loan Channel Partner"
        ogDescription="Become an Aham Housing Finance channel partner. Earn attractive payouts on home loan leads with simple onboarding, fast approvals and full sales support."
      />
      {/* --------------------------
    Banner Section
--------------------------- */}
      {/* ===== Hero Banner Section ===== */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Background Image & Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${bannerImage}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-4 md:mb-6 leading-tight">
              {banner ? banner.title : "Leadership & Vision"}
            </h1>
            <p className="text-lg md:text-2xl text-white/90 font-medium max-w-2xl mx-auto leading-relaxed mb-6 md:mb-8 text-center">
              {banner ? banner.subtitle : "Meet our Leadership Team & Board of Directors"}
            </p>

            {/* Breadcrumb Navigation */}
            <div className="flex items-center justify-center space-x-2">
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Our Team </span>
            </div>
          </motion.div>
          {/* Hidden SEO Image for Alt Tag requirement */}
          <img
            src={bannerImage}
            alt="Aham Housing Finance channel partner program – home loan partnership opportunity for DSAs and connectors"
            className="hidden"
          />
        </div>
      </section>

      {/* --------------------------
    BUTTONS BELOW BANNER
--------------------------- */}
      <div className="flex justify-center mt-[-30px] md:mt-[-40px] mb-12 md:mb-16 z-20 relative px-4">
        <div className="bg-white/90 backdrop-blur-lg p-2 md:px-6 md:py-3 rounded-2xl md:rounded-full shadow-xl flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto items-center">

          <button
            className={`w-full sm:w-auto px-4 py-1.5 md:px-8 md:py-3 rounded-xl md:rounded-full font-semibold transition text-sm md:text-base ${activeTab === "team"
              ? "bg-blue-700 text-white shadow-lg"
              : "bg-white text-blue-700 border border-blue-200"
              }`}
            onClick={() => setActiveTab("team")}
          >
            Management Team
          </button>

          <button
            className={`w-full sm:w-auto px-4 py-1.5 md:px-8 md:py-3 rounded-xl md:rounded-full font-semibold transition text-sm md:text-base ${activeTab === "directors"
              ? "bg-blue-700 text-white shadow-lg"
              : "bg-white text-blue-700 border border-blue-200"
              }`}
            onClick={() => setActiveTab("directors")}
          >
            Board of Directors
          </button>

        </div>
      </div>

      {/* --------------------------
          MANAGEMENT TEAM SECTION
      --------------------------- */}
      {activeTab === "team" && (
        <section className="py-12 md:py-20 px-4 md:px-6 lg:px-24 bg-gradient-to-b from-blue-50 via-white to-blue-100">
          <div className="container mx-auto relative z-10">
            <h2 className="text-3xl md:text-5xl font-extrabold text-center mb-10 md:mb-16">
              Our Management Team
            </h2>

            <div className="grid gap-8 md:gap-12 md:grid-cols-2 lg:grid-cols-3 place-items-center">
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-sm"
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-40 h-40 rounded-full mx-auto mb-6 border-4 border-white shadow-xl object-cover object-top"
                    onError={(e) => { e.target.onerror = null; e.target.src = "/img/placeholder_image.webp" }}
                  />

                  <h3 className="text-xl font-extrabold text-gray-900 text-center">
                    {member.name}
                  </h3>
                  <p className="text-blue-700 font-medium text-sm text-center mb-4">
                    {member.designation}
                  </p>
                  <div className="p-3 text-center">
                    <button
                      onClick={() => setSelectedMember(member)}
                      className="mt-4 text-red-600 hover:text-blue-800 text-sm font-semibold"
                    >
                      View Profile →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* --------------------------
          DIRECTORS SECTION
      --------------------------- */}
      {activeTab === "directors" && (
        <section className="relative py-12 md:py-20 px-4 md:px-6 lg:px-24 bg-gradient-to-b from-blue-50 via-white to-blue-100">
          <h2 className="text-3xl md:text-5xl font-extrabold text-center mb-10 md:mb-16">
            Board of Directors
          </h2>

          <div className="grid gap-8 md:gap-12 md:grid-cols-2 lg:grid-cols-3 place-items-center">
            {directors.map((director) => (
              <div
                key={director.id}
                className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-sm"
              >
                <img
                  src={director.image}
                  alt={director.name}
                  className="w-40 h-40 rounded-full mx-auto mb-6 border-4 border-white shadow-xl object-cover object-top"
                  onError={(e) => { e.target.onerror = null; e.target.src = "/img/placeholder_image.webp" }}
                />

                <h3 className="text-xl font-extrabold text-gray-900 text-center">
                  {director.name}
                </h3>
                <p className="text-blue-700 font-medium text-sm text-center mb-4">
                  {director.designation}
                </p>
                <div className="p-3 text-center">
                  <button
                    onClick={() => setSelectedDirector(director)}
                    className="mt-4 text-red-600 hover:text-blue-800  text-sm font-semibold"
                  >
                    View Profile →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* --------------------------
          TEAM MEMBER MODAL
      --------------------------- */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[9999]">
          <div className="bg-white rounded-3xl p-5 md:p-8 max-w-5xl w-[92%] md:w-[90%] relative shadow-2xl overflow-y-auto max-h-[65vh] md:max-h-[85vh]">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-primary-600 transition-colors text-3xl z-30"
              onClick={() => setSelectedMember(null)}
            >
              ✕
            </button>

            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              <div className="w-full md:w-auto flex justify-center flex-shrink-0">
                <img
                  src={selectedMember.image}
                  className="w-32 h-32 md:w-64 md:h-80 object-cover object-top rounded-full md:rounded-3xl shadow-2xl"
                  alt={selectedMember.name}
                  onError={(e) => { e.target.onerror = null; e.target.src = "/img/placeholder_image.webp" }}
                />
              </div>

              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-bold text-blue-700 mb-1">
                  {selectedMember.name}
                </h3>
                <p className="text-gray-600 font-medium mb-6 text-sm md:text-lg">
                  {selectedMember.designation}
                </p>

                {selectedMember.bio.map((para, i) => (
                  <p key={i} className="text-gray-700 mb-3 leading-relaxed text-sm md:text-base">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --------------------------
          DIRECTOR MODAL
      --------------------------- */}
      {selectedDirector && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[9999]">
          <div className="bg-white rounded-3xl p-5 md:p-8 max-w-5xl w-[92%] relative shadow-2xl overflow-y-auto max-h-[65vh] md:max-h-[85vh]">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-primary-600 transition-colors text-3xl z-30"
              onClick={() => setSelectedDirector(null)}
            >
              ✕
            </button>

            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              <div className="w-full md:w-auto flex justify-center flex-shrink-0">
                <img
                  src={selectedDirector.image}
                  className="w-32 h-32 md:w-64 md:h-80 object-cover object-top rounded-full md:rounded-3xl shadow-2xl"
                  alt={selectedDirector.name}
                  onError={(e) => { e.target.onerror = null; e.target.src = "/img/placeholder_image.webp" }}
                />
              </div>

              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-bold text-blue-700 mb-1">
                  {selectedDirector.name}
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-sm md:text-lg">
                  {selectedDirector.designation}
                </p>

                {selectedDirector.bio.map((para, i) => (
                  <p key={i} className="text-gray-700 mb-3 leading-relaxed text-sm md:text-base">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeadershipPage;
